import { z } from "zod";
import { idString, IsoDate, MediumText, ShortText } from "./common.js";
export const TIMELINE_MILESTONE_KINDS = [
    "last_known_good",
    "occurred",
    "recognised",
    "solving",
    "containment_started",
    "countermeasure",
    "custom",
];
export const TimelineMilestoneKindSchema = z.enum(TIMELINE_MILESTONE_KINDS);
export const TimelineMilestoneSchema = z
    .object({
    id: idString,
    kind: TimelineMilestoneKindSchema,
    label: ShortText,
    date: IsoDate,
    endDate: IsoDate.optional(),
    estimated: z.boolean().optional(),
    projected: z.boolean().optional(),
})
    .strict();
export const ProblemTimelineParamsSchema = z
    .object({
    milestones: z.array(TimelineMilestoneSchema).max(100),
    exposureRatePerDay: z.number().min(0).optional(),
    estimatedExposure: z.number().min(0).optional(),
    exposureBasis: MediumText.optional(),
    exposureUnit: ShortText.optional(),
})
    .strict();
export const defaultProblemTimelineParams = {
    milestones: [],
    exposureUnit: "units",
};
export function computeSuspectWindow(timeline) {
    const lastKnownGood = timeline.milestones.find((milestone) => milestone.kind === "last_known_good");
    const occurred = timeline.milestones.find((milestone) => milestone.kind === "occurred");
    const recognised = timeline.milestones.find((milestone) => milestone.kind === "recognised");
    const startIso = lastKnownGood?.date ?? occurred?.date ?? null;
    const endIso = recognised?.date ?? null;
    const start = startIso ? parseIsoDay(startIso) : null;
    const end = endIso ? parseIsoDay(endIso) : null;
    const issues = [];
    if (start === null) {
        issues.push("Add the last-known-good or problem occurrence date.");
    }
    if (end === null)
        issues.push("Add when the problem was first recognised.");
    if (start !== null && end !== null && end < start) {
        issues.push("First recognised date is before the suspect-window start.");
    }
    const durationDays = start !== null && end !== null && end >= start
        ? Math.round((end - start) / 86_400_000)
        : null;
    const exposure = timeline.estimatedExposure ??
        (durationDays !== null && timeline.exposureRatePerDay !== undefined
            ? durationDays * timeline.exposureRatePerDay
            : null);
    return {
        startIso,
        endIso,
        durationDays,
        exposure,
        exposureUnit: timeline.exposureUnit?.trim() || "units",
        issues,
    };
}
export function summarizeSuspectWindow(window) {
    if (window.durationDays === null)
        return null;
    const base = `${window.durationDays}-day suspect window`;
    if (window.exposure === null)
        return base;
    return `${base} · ≈${formatNumber(window.exposure)} ${window.exposureUnit} exposed`;
}
function parseIsoDay(value) {
    if (!IsoDate.safeParse(value).success)
        return null;
    const parsed = Date.parse(`${value}T00:00:00.000Z`);
    return Number.isNaN(parsed) ? null : parsed;
}
function formatNumber(value) {
    return new Intl.NumberFormat(undefined, {
        maximumFractionDigits: value < 10 ? 1 : 0,
    }).format(value);
}
//# sourceMappingURL=problemTimeline.js.map